import { AlertTriangle, CloudOff, Inbox, Lock, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from './button'
import { cn } from '@/lib/utils'

/* ============================================================================
   The six mandatory screen states.

   Screen Inventory OCST/KN/26-SCR §6 states these once for all 88 screens
   rather than redrawing them 88 times. They live here for the same reason: a
   screen that hand-rolls its own empty state will drift, and the drift is only
   ever noticed by the user who hits it.

     loading        skeleton, never a spinner on a full page
     empty          nothing to show, and it is not an error
     error          something failed, with a way to retry
     no-permission  the user is authenticated but not authorised
     offline        mobile: working from the local queue
     stale          showing cached data that is older than it should be

   EVERY screen must handle all six. A screen that only handles loading and
   success is not finished.
   ========================================================================= */

/* ---- 1. loading ---------------------------------------------------------
   A skeleton, not a spinner. A spinner tells the user to wait; a skeleton
   tells them what they are waiting for, and on a 3G handset in a service
   corridor that difference is the whole experience. */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'animate-pulse rounded-md bg-[color-mix(in_oklab,var(--hk-muted-fg)_18%,transparent)]',
        className,
      )}
    />
  )
}

export function LoadingState({ rows = 5, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy className="flex flex-col gap-2 p-4">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <Skeleton className="h-3 grow" />
          <Skeleton className="h-3 w-16 shrink-0" />
        </div>
      ))}
    </div>
  )
}

/* ---- shared frame for the message states ------------------------------- */

function StateFrame(
  { icon, title, body, action, tone = 'muted' }:
  { icon: ReactNode; title: ReactNode; body?: ReactNode; action?: ReactNode
    tone?: 'muted' | 'danger' | 'warning' },
) {
  const ring = {
    muted:   'text-muted-fg bg-muted',
    danger:  'text-[#b91c1c] bg-[#fee2e2] dark:text-[#fca5a5] dark:bg-[#2a0f0f]',
    warning: 'text-[#c2410c] bg-[#ffedd5] dark:text-[#fdba74] dark:bg-[#2a1508]',
  }[tone]
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className={cn('flex size-12 items-center justify-center rounded-full', ring)}>{icon}</div>
      <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
      {body && <p className="max-w-sm text-sm text-muted-fg">{body}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}

/* ---- 2. empty ------------------------------------------------------------
   Empty is not failure and must not look like it. Where the user can create
   the missing thing, offer it here — an empty list with no way forward is a
   dead end. */

export function EmptyState(
  { title, body, action, icon }:
  { title: ReactNode; body?: ReactNode; action?: ReactNode; icon?: ReactNode },
) {
  return <StateFrame icon={icon ?? <Inbox className="size-5" />} title={title} body={body} action={action} />
}

/* ---- 3. error ------------------------------------------------------------
   Always offer a retry. Never show the raw exception to a housekeeper; put the
   correlation id in small print so support can find it. */

export function ErrorState(
  { title = 'Something went wrong', body, onRetry, correlationId }:
  { title?: ReactNode; body?: ReactNode; onRetry?: () => void; correlationId?: string },
) {
  return (
    <StateFrame
      tone="danger"
      icon={<AlertTriangle className="size-5" />}
      title={title}
      body={
        <>
          {body ?? 'The information could not be loaded. This is usually temporary.'}
          {correlationId && (
            <span className="mt-2 block font-mono text-[11px] opacity-70">{correlationId}</span>
          )}
        </>
      }
      action={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw /> Try again
          </Button>
        )
      }
    />
  )
}

/* ---- 4. no permission ----------------------------------------------------
   Distinct from error, and it must be. The user signed in successfully; they
   simply may not see this. Naming who can grant access turns a dead end into
   a next step — and this state is also what a new joiner sees on their first
   morning, in the window between the PMS creating them and an administrator
   assigning a role (TQ-17). */

export function NoPermissionState(
  { body = 'You are signed in, but this area is not part of your role. Your supervisor can request access for you.' }:
  { body?: ReactNode } = {},
) {
  return (
    <StateFrame
      icon={<Lock className="size-5" />}
      title="You do not have access to this"
      body={body}
    />
  )
}

/* ---- 5. offline ----------------------------------------------------------
   Mobile. Never block the user: housekeeping happens in stairwells and service
   corridors, and an attendant who cannot tick a checklist because the signal
   dropped will stop using the product. Show the queue depth so they can see
   their work is held, not lost. */

export function OfflineBanner(
  { queued, label = 'Working offline', queuedLabel }:
  {
    queued?: number
    /** Translated. Defaults to English so the banner is usable before i18n is wired. */
    label?: ReactNode
    /** Translated, already carrying the count — the number's position differs by
     *  language, so the caller formats the whole phrase rather than passing a digit
     *  this component then has to place. */
    queuedLabel?: ReactNode
  } = {},
) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-medium',
        'border-[#fdba74] bg-[#fff7ed] text-[#c2410c]',
        'dark:border-[#7c3a10] dark:bg-[#2a1c0d] dark:text-[#fdba74]',
      )}
    >
      <CloudOff className="size-3.5 shrink-0" />
      <span>{label}</span>
      {queued !== undefined && queued > 0 && (
        <span className="ms-auto tnum opacity-90">{queuedLabel ?? `${queued} waiting to send`}</span>
      )}
    </div>
  )
}

/* ---- 6. stale ------------------------------------------------------------
   Shown when the data on screen is older than its refresh interval — a room
   board that has silently stopped updating is worse than one that says so,
   because a supervisor will act on it. */

export function StaleBanner(
  { since, onRefresh, message, refreshLabel = 'Refresh' }:
  {
    since: string
    onRefresh?: () => void
    /** Translated, already carrying the time. Defaults to English. */
    message?: ReactNode
    refreshLabel?: ReactNode
  },
) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11.5px]',
        'border-[#fcd34d] bg-[#fef3c7] text-[#b45309]',
        'dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]',
      )}
    >
      <AlertTriangle className="size-3.5 shrink-0" />
      <span>{message ?? <>Showing information from {since}. It may have changed.</>}</span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          className="ms-auto inline-flex items-center gap-1 font-medium underline cursor-pointer"
        >
          <RefreshCw className="size-3" /> {refreshLabel}
        </button>
      )}
    </div>
  )
}
